import { queryOptions } from '@tanstack/react-query'
import { apiGet, apiPost, type PostValue } from './client'
import type { TablePage, TableRequest } from './softsee'

/** Productivitee - /api/v2/productivitee/*; perechile vechi sunt in `ProductiviteeController`. */

function params(r: Omit<TableRequest, 'list'>, extra: Record<string, string> = {}): URLSearchParams {
  const p = new URLSearchParams()
  p.set('draw', String(r.draw))
  p.set('start', String(r.start))
  p.set('length', String(r.length))
  p.set('order[0][column]', String(r.orderColumn))
  p.set('order[0][dir]', r.orderDir)
  p.set(`columns[${r.orderColumn}][name]`, r.orderName)
  p.set('search[value]', r.search)
  for (const [k, v] of Object.entries(extra)) p.set(k, v)
  return p
}

export type Row = Record<string, unknown>
export type Req = Omit<TableRequest, 'list'>

/**
 * Lista masinilor, cand iese goala, spune si DE CE: `Productivitee::emptyHint`
 * numara scara de porti prin care trece ea si nu trece lista oamenilor (ziua,
 * `removed`, `Magazie`, `Agent`, `Compliance`, utilizatorul logat, sediul) si
 * scrie prima care cade la zero. Lipseste cand chiar n-a lucrat nimeni sau
 * cand cererea are o cautare in ea.
 */
export type ProductiviteePage = TablePage<Row> & { emptyHint?: string }

/** Cele trei liste au aceeasi forma de cheie, ca `ListPage` din productivitee/Pages.tsx sa le primeasca pe toate. */
function listQuery(what: string, r: Req, extra: Record<string, string>) {
  const key: readonly unknown[] = ['productivitee', what, r, extra]
  return queryOptions({ queryKey: key, queryFn: () => apiGet<ProductiviteePage>(`/productivitee/${what}?${params(r, extra)}`) })
}

export function devicesTableQuery(r: Req & { date: string }) {
  return listQuery('devices-table', r, { date: r.date })
}

export function usersTableQuery(r: Req & { date: string }) {
  return listQuery('users-table', r, { date: r.date })
}

export function usersPeriodTableQuery(r: Req & { from: string; to: string }) {
  return listQuery('users-period-table', r, { from: r.from, to: r.to })
}

/** O masina in graficul "Device Working Time" - `Productivity::workingTimeDevices`. */
export interface WorkingTime { name: string; active_time: number; inactive_time: number }

/** O masina in "Top Productive" / "Top Unproductive" - `Productivity::productiveTopDevices`. */
export interface TopDevice { name: string; value: number }

/**
 * Acelasi lucru, pe OM.
 *
 * Poarta si `id`, pe care randul masinii nu-l are nevoie: un utilizator fara
 * nume nu lasa eticheta goala sub coloana lui, ci scrie "no name (#4)", ca
 * `odDeviceName` in portal. Fara numar n-ar avea ce pune in paranteza.
 */
export interface WorkingTimeUser extends WorkingTime { id: number }
export interface TopUser extends TopDevice { id: number }

/**
 * Tabloul: cele patru cifre SI randurile celor SASE grafice.
 *
 * Portalul vechi le cerea pe patru drumuri (`getDashboard`, `getWorktime`,
 * `getProductiveData`, `getUnproductiveData`); aici vin pe unul singur, fiindca
 * ecranul le cere pe toate odata, la fiecare minut.
 *
 * Cele trei ale oamenilor (21 septembrie 2026) au campuri SEPARATE, nu aceleasi
 * trei cu un comutator: se citesc din alte tabele decat cele ale masinilor -
 * `it_logged_users_list` cu `it_productivitee_user_summary` - si intr-o zi in
 * care una din parti n-are nimic, cealalta poate fi plina.
 */
export interface Dashboard {
  productivity: number
  productive_time: number
  start_time: string | null
  inactive_time: number
  working_time: WorkingTime[]
  top_productive: TopDevice[]
  top_unproductive: TopDevice[]
  user_working_time: WorkingTimeUser[]
  user_top_productive: TopUser[]
  user_top_unproductive: TopUser[]
}

export function dashboardQuery() {
  /* index-1.js reincarca tabloul la fiecare minut (`setInterval(refreshDashboard, 60000)`). */
  return queryOptions({ queryKey: ['productivitee', 'dashboard'], queryFn: () => apiGet<Dashboard>('/productivitee/dashboard'), refetchInterval: 60_000 })
}

/** [latime in px, fel] - 0 untagged, 1 productive, 2 unproductive. */
export type Segment = [number, number]
export interface App { application_id?: number; application_name: string; app_time: number }
export interface AppLists { productive: App[]; unproductive: App[]; untagged: App[] }
/**
 * Un gol explicat de om in "Your day" (`it_productivitee_day_entries`), pe
 * banda zilei: orele `HH:MM`, felul si numele lui (`EntryKind::label`) si
 * explicatia scrisa de om (`note`, text simplu; poate lipsi sau fi goala).
 */
export interface ExplainedSpan { start: string; end: string; kind: number; label: string; note?: string }

export interface DeviceSummary {
  date: string
  ranking: number
  productivity: number
  officeTime: number
  startingTime: string | null
  workingTime: number
  activeTime: number
  inactiveTime: number
  chart: Segment[]
  applications: AppLists
  /** Explicatiile conturilor intrate pe masina in zi, si secundele lor reunite. */
  explained?: ExplainedSpan[]
  explainedTime?: number
}

export function deviceSummaryQuery(id: number, date: string) {
  return queryOptions({ queryKey: ['productivitee', 'devices', id, 'summary', date], queryFn: () => apiGet<DeviceSummary>(`/productivitee/devices/${id}/summary?date=${date}`) })
}

export function historyQuery(id: number, date: string) {
  return queryOptions({ queryKey: ['productivitee', 'devices', id, 'history', date], queryFn: () => apiGet<{ date: string; rows: Row[] }>(`/productivitee/devices/${id}/history?date=${date}`) })
}

export interface TaggedApp { ID: number; application_name: string; application_type: string; tag_type: number }

export function applicationsQuery(id: number) {
  return queryOptions({ queryKey: ['productivitee', 'devices', id, 'applications'], queryFn: () => apiGet<{ group: number; rows: TaggedApp[] }>(`/productivitee/devices/${id}/applications`) })
}

/**
 * `level` (it_productivitee_device_settings.level): 1 Basic, 2 Extended. Vine
 * langa restrictii si langa lista de blocate, fiindca pe Basic agentul nu
 * citeste titlul ferestrei si blocarea de web (site) e muta - ecranul o
 * dezactiveaza si o spune. Blocarea de aplicatii (exe) merge pe ambele.
 */
export function nestedTableQuery(id: number, what: 'restrictions' | 'events', r: Req) {
  return queryOptions({ queryKey: ['productivitee', 'devices', id, what, r], queryFn: () => apiGet<TablePage<Row> & { level?: number }>(`/productivitee/devices/${id}/${what}/table?${params(r)}`) })
}

export function blockedQuery(id: number) {
  return queryOptions({ queryKey: ['productivitee', 'devices', id, 'blocked'], queryFn: () => apiGet<{ rows: Row[]; level?: number }>(`/productivitee/devices/${id}/blocked`) })
}

export interface BusinessDay { day_id: number; day_of_week: string; start_hour: string; end_hour: string; break: number; is_business: boolean }
export interface DeviceSettings { method: number; calculate_out_of_office: boolean; methods: { ID: number; method_name: string }[]; businessHours: BusinessDay[] }

export function deviceSettingsQuery(id: number) {
  return queryOptions({ queryKey: ['productivitee', 'devices', id, 'settings'], queryFn: () => apiGet<DeviceSettings>(`/productivitee/devices/${id}/settings`) })
}

export interface UserTimes { starting_time: string | null; ending_time: string | null; working_time: number; active_time: number; inactive_time: number }
/**
 * `user_id`/`username`: contul cu care s-a lucrat pe masina. Randul listei e al
 * OMULUI (toate conturile aceluiasi proprietar de aparat), deci masinile din
 * detaliu pot fi ale unor conturi diferite.
 */
export interface UserDevice { device_id: number; device_name: string; GroupID: number; user_id?: number; username?: string }
/**
 * Sub fiecare masina, sectiunea per-device arata ACELEASI carduri ca fila
 * aparatului (Device Expand): cele 4 timpuri (start/working/active/inactive) plus
 * Productivity si Office ranking. `startingTime` e deja "HH:MM", timpii in secunde.
 */
export interface UserSummaryDevice extends UserDevice {
  ranking: number
  productivity: number
  startingTime: string | null
  workingTime: number
  activeTime: number
  inactiveTime: number
  chart: Segment[]
}
/** `explained`: explicatiile OMULUI (toate conturile lui din zi), aceleasi pe fiecare banda de masina. */
export interface UserSummary { date: string; summary: UserTimes | null; devices: UserSummaryDevice[]; explained?: ExplainedSpan[]; explained_time?: number }

export function userSummaryQuery(id: number, date: string) {
  return queryOptions({ queryKey: ['productivitee', 'users', id, 'summary', date], queryFn: () => apiGet<UserSummary>(`/productivitee/users/${id}/summary?date=${date}`) })
}

export function userApplicationsQuery(id: number, date: string) {
  return queryOptions({ queryKey: ['productivitee', 'users', id, 'applications', date], queryFn: () => apiGet<{ date: string; devices: (UserDevice & { applications: AppLists })[] }>(`/productivitee/users/${id}/applications?date=${date}`) })
}

/**
 * Summary-ul randului desfacut de pe lista de PERIOADA, pentru ziua randului.
 * ACEEASI forma ca `userSummary`, fiindca e acelasi capat: pe 22 septembrie
 * 2026 sectiunea per-masina a primit cardurile filei aparatului numai pe
 * `/user_productivity`, si de atunci ecranul de perioada arata alt Summary
 * pentru aceeasi zi si acelasi om.
 */
export function userPeriodSummaryQuery(id: number, from: string) {
  return queryOptions({ queryKey: ['productivitee', 'users', id, 'period-summary', from], queryFn: () => apiGet<UserSummary>(`/productivitee/users/${id}/period-summary?from=${from}`) })
}

export function userPeriodApplicationsQuery(id: number, from: string) {
  return queryOptions({ queryKey: ['productivitee', 'users', id, 'period-applications', from], queryFn: () => apiGet<{ date: string; devices: (UserDevice & { applications: AppLists })[] }>(`/productivitee/users/${id}/period-applications?from=${from}`) })
}

/* ------------------------------------------------------- evolutia in timp */

/**
 * O treapta a perioadei: o zi, o saptamana sau o luna. `days` sunt zilele CU
 * RAPORT din ea, nu zilele ei - o saptamana cu trei zile raportate si patru
 * tacute nu e o saptamana plina, si aici se vede.
 */
export interface Bucket {
  key: string
  label: string
  from: string
  to: string
  working_time: number
  active_time: number
  inactive_time: number
  days: number
  /** Timpul activ din timpul de lucru, in procente - socotit pe server, o singura data. */
  share: number
  /** Numai pe tabloul de conducere: cati oameni DEOSEBITI au raportat in treapta. */
  people?: number
}

/**
 * Cifrele unei perioade intregi: SUMA, si MEDIA PE ZI din ea.
 *
 * `days` e numitorul amandurora, si e singurul numitor care se foloseste:
 * ZILELE CU RAPORT, nu zilele din calendar (pe tabloul unei firme, zilele-om).
 * O luna cu doua sarbatori si un concediu are douazeci si doua de zile in
 * calendar si douasprezece cu rezumat, iar impartirea la douazeci si doua ar
 * scrie ca omul a lucrat mai putin pe zi - fals, si fix in dauna lui. Aceeasi
 * hotarare ca la mediana grupului.
 *
 * Mediile se socotesc pe SERVER (`Productivitee::withAverages`), nu aici: cifra
 * iese o data si e aceeasi oriunde e citita. `null` inseamna "nicio zi cu
 * raport" - nu zero.
 */
export interface Totals {
  working_time: number
  active_time: number
  inactive_time: number
  days: number
  share: number
  people?: number
  avg_working_time: number | null
  avg_active_time: number | null
  avg_inactive_time: number | null
}

/**
 * Mediana grupului pe o treapta. `people` sunt oamenii care AU RAPORTAT in
 * treapta aia - cine a lipsit nu intra cu zero, altfel un concediu ar cobori
 * grupul. `null` inseamna "n-a raportat nimeni", nu "zero".
 */
export interface MedianBucket {
  key: string
  label: string
  people: number
  working_time: number | null
  active_time: number | null
  share: number | null
}

/**
 * Grupul cu care se compara omul.
 *
 * `source`: `department` - departamentul PERSOANEI din structura firmei
 * (`it_departamente`, prin `persoane_contact_fixe.logon_user`); `group` -
 * grupul MASINII pe care a stat cele mai multe zile (`it_echipamente.GroupID`),
 * cand structura firmei nu stie de el; `none` - niciunul.
 *
 * `comparable` e fals sub trei oameni cu date in perioada, si atunci nu se
 * deseneaza nimic: cu doi, "mediana grupului" e chiar celalalt om.
 */
export interface PeerGroup {
  id: number
  name: string
  source: 'department' | 'group' | 'none'
  people: number
  comparable: boolean
  median: MedianBucket[]
}

/** Mediana omului peste treptele unei perioade - treptele fara raport nu intra. */
export interface OwnMedian {
  share: number | null
  working_time: number | null
  active_time: number | null
  inactive_time: number | null
}

/** Evolutia unui om - fila Evolution din randul desfacut al listei User Productivity. */
export interface UserEvolution {
  from: string
  to: string
  grain: 'day' | 'week' | 'month'
  buckets: Bucket[]
  previous: { from: string; to: string; buckets: Bucket[] }
  totals: Totals
  previous_totals: Totals
  reported_days: number
  period_days: number
  group: PeerGroup
  own_median: OwnMedian
  own_median_previous: OwnMedian
}

export function userEvolutionQuery(id: number, from: string, to: string) {
  return queryOptions({
    queryKey: ['productivitee', 'users', id, 'evolution', from, to],
    queryFn: () => apiGet<UserEvolution>(`/productivitee/users/${id}/evolution?from=${from}&to=${to}`),
  })
}

/** Un sertar al tabloului de conducere: un sediu sau un departament. Niciodata un om. */
export interface BoardSlice {
  rank: number
  id: number
  name: string
  people: number
  working_time: number
  active_time: number
  inactive_time: number
  share: number
  prev_people: number
  prev_working_time: number
  prev_active_time: number
  prev_share: number
  delta: number
  /**
   * Cate capete (om, zi) au raportat in sertar - numitorul mediilor de dedesubt.
   * Nu zile de calendar: un sediu cu zece oameni are zece zile-om intr-o zi.
   */
  person_days: number
  prev_person_days: number
  /** Media pe zi-om, socotita pe server. `null` = niciun cap raportat. */
  avg_working_time: number | null
  avg_active_time: number | null
  prev_avg_working_time: number | null
  prev_avg_active_time: number | null
}

/**
 * Cat de gaurita e masuratoarea. Un procent socotit peste jumatate de firma care
 * n-a raportat e o minciuna spusa cu incredere - deci cifrele astea stau pe
 * ecran, nu intr-o nota de subsol.
 *
 * Aici statea si impartirea timpului masurat pe eticheta (productiv /
 * neproductiv / neetichetat). A plecat pe 24 septembrie 2026: era o suma peste
 * toata firma fara numitor, iar de la aceeasi data randul fara eticheta se scrie
 * in istoric cu `tag = 1`, deci felia "neetichetat" ar fi fost de acum incolo
 * mereu zero si cea "productiv" umflata cu tot timpul neetichetat. In locul ei,
 * `top_apps`.
 */
export interface BoardHonesty {
  people_reported: number
  people_expected: number
  devices_reported: number
  devices_expected: number
}

/**
 * DIN CE TABEL VIN ZILELE PERIOADEI.
 *
 * Ziua care e in rollup-ul pe (om, zi) se citeste de acolo - aceeasi REUNIUNE de
 * intervale pe care o arata si lista User Productivity. Ziua care nu e inca in el
 * se citeste din `it_productivitee_user_summary`, tabelul vechi scris prin SUMA
 * duratelor. Cifrele celor doua nu se pot impaca, deci cand perioada le amesteca
 * forma graficului se schimba la mijloc, si ecranul trebuie sa spuna de ce.
 *
 * Se numara numai zilele CU DATE (`reported_days`): o sambata fara niciun rand
 * n-are de unde sa vina, deci n-are nicio provenienta de spus.
 */
export interface BoardSources {
  days: number
  reported_days: number
  rollup_days: number
  legacy_days: number
  legacy_from: string
  legacy_to: string
}

/** O treapta a unui panou. `share` e null cand nimeni n-a raportat in ea - GOL, nu zero. */
export interface BoardPanelPoint {
  key: string
  label: string
  share: number | null
  days: number
}

/**
 * UN DEPARTAMENT, FATA DE EL INSUSI - panoul mic din cardul care a luat locul
 * clasamentului de departamente.
 *
 * Clasamentul spunea cine e primul, si raspunsul nu se schimba niciodata: era
 * dominat de masini uitate aprinse. Panoul spune cine s-a SCHIMBAT.
 *
 * `median` e mediana treptelor DINAINTE ale departamentului, `spread` cat se
 * clatina el de obicei (abaterea mediana absoluta), `jump` cat s-a abatut
 * treapta de acum de la mediana lui, si `jumped` e adevarat cand mutarea e de
 * cateva ori cat imprastierea LUI. Nu exista niciun prag comun: un prag comun ar
 * fi colorat mereu aceleasi departamente, adica ar fi repetat clasamentul cu
 * alte culori.
 *
 * `comparable` e fals sub trei oameni cu date in perioada, si atunci panoul nu
 * are linie - aceeasi limita ca la mediana grupului unui om. `scored` e fals sub
 * sase trepte cu raport: linia se deseneaza, semnul de "a sarit" nu.
 */
export interface BoardPanel {
  id: number
  name: string
  people: number
  comparable: boolean
  points: BoardPanelPoint[]
  reported_buckets: number
  latest: number | null
  median: number | null
  spread: number | null
  jump: number | null
  jumped: boolean
  scored: boolean
}

/** O masina pe harta "cine tace": zilele in care CHIAR a scris un rand. */
export interface BoardSilenceDevice {
  device_id: number
  name: string
  reported: number
  last_day: string | null
  days: string[]
}

/**
 * CINE TACE, SI DE CAND.
 *
 * O celula pe (masina, zi), din PREZENTA randului in rollup-ul pe (masina, zi).
 * Zero aritmetica de timp: nu se aduna si nu se imparte nimic, se intreaba doar
 * daca randul exista - de-aia cifra de aici nu poate fi gresita.
 *
 * `by_day` spune cate masini au raportat in fiecare zi, peste TOATE cele
 * asteptate. Zero intr-o zi nu inseamna ca au tacut o suta de masini, inseamna
 * ca n-a scris nimeni: o cadere de colectare, nu de masini - si coloana aia
 * ramane neutra in loc sa se aprinda.
 *
 * `trimmed` e adevarat cand perioada ecranului e mai lunga decat incape pe harta
 * si s-au luat ultimele zile din ea.
 */
export interface BoardSilence {
  from: string
  to: string
  trimmed: boolean
  days: string[]
  limit: number
  expected: number
  silent: number
  devices: BoardSilenceDevice[]
  by_day: { day: string; devices: number }[]
}

/**
 * O aplicatie a firmei pe care nimeni nu a etichetat-o, cu timpul ei din fereastra.
 *
 * `app_time` e SUMA ferestrei peste toata firma si nu se mai deseneaza; pe rand
 * sta `avg_time`, media pe zi-om, cu numitorul (`person_days`) scris pe card.
 * "2 zile 3h" nu spunea daca merita apasat butonul: pe trei sute de oameni aia e
 * un program deschis din greseala, pe trei e jumatate din munca lor.
 */
export interface UntaggedApp {
  application_id: number
  application_name: string
  application_type: string
  app_time: number
  /** Media pe zi-om a ferestrei, socotita pe server. `null` = niciun cap raportat. */
  avg_time: number | null
}

/** O aplicatie din clasamentul "unde se duce ziua", cu timpul ei din fereastra. */
export interface TopApp {
  application_id: number
  application_name: string
  application_type: string
  app_time: number
  /** Media pe zi-om a ferestrei, socotita pe server. `null` = niciun cap raportat. */
  avg_time: number | null
  /** Are eticheta pe vreun grup? Se SPUNE, nu se filtreaza pe ea. */
  tagged: boolean
}

/**
 * UNDE SE DUCE ZIUA ECHIPEI.
 *
 * A luat locul cardului "Measured time" - trei durate adunate peste toata firma,
 * fara numitor, si citite din `it_productivitee_history.tag`, care de pe 24
 * septembrie 2026 nu mai poate spune ce e neetichetat.
 *
 * `rows` sunt aplicatiile cu cel mai mult timp, taiate la cateva. `total` si
 * `apps` NU sunt ale lor: sunt ale ferestrei intregi, socotite inaintea taierii,
 * deci ecranul poate spune si cat ia fiecare rand din zi, si cat a ramas in
 * coada.
 *
 * `total` e suma reuniunilor PE APLICATIE, nu reuniunea peste toate: o secunda
 * in care masina a raportat doua programe deodata intra in amandoua, deci
 * totalul poate fi mai lung decat cat a lucrat cineva. Scrie pe card.
 *
 * `from`/`to` sunt fereastra ei, de cel mult `days` zile - mai SCURTA decat cea a
 * ecranului, fiindca e una din cele doua citiri care ating tabelul cel mare -
 * iar `cached_seconds` spune cat de veche poate fi cifra.
 *
 * `trimmed` spune daca fereastra e CHIAR mai scurta decat perioada aleasa. Pe
 * "Last 7 days" nu e, si atunci cardul n-are voie sa scrie "nu toata perioada":
 * pana pe 25 septembrie 2026 o scria oricum, deci minta pe doua din cele patru
 * pastile de sus.
 *
 * `person_days` e numitorul tuturor mediilor de aici: capetele (om, zi) care au
 * raportat IN FERESTRA, nu in toata perioada - sapte zile de timp impartite la
 * treizeci de zile de oameni ar fi dat o cifra de patru ori mai mica.
 *
 * `untagged_time` / `untagged_apps` / `untagged_share`: cat din fereastra s-a dus
 * in programe care n-au eticheta pe niciun grup. E felia de onestitate care a
 * plecat pe 24 septembrie 2026 impreuna cu cardul "Measured time", intoarsa
 * socotita CINSTIT: din tabelul de ETICHETARE si peste toate aplicatiile
 * ferestrei, nu din `it_productivitee_history.tag`, unde randul fara eticheta se
 * scrie cu `tag = 1` si felia ar fi fost de acum incolo mereu zero.
 */
export interface BoardTopApps {
  from: string
  to: string
  days: number
  trimmed: boolean
  cached_seconds: number
  total: number
  apps: number
  untagged_time: number
  untagged_apps: number
  untagged_share: number
  person_days: number
  avg_total: number | null
  rows: TopApp[]
}

/**
 * TOP APLICATII FARA ETICHETA.
 *
 * "Neetichetat" e o stare a CATALOGULUI (`it_productivitee_applications_list`
 * fara linie de etichetare), nu a istoricului: randul de istoric fara eticheta se
 * scrie cu `tag = 1` si se socoteste productiv, deci acolo "neetichetat" nu mai
 * exista. `catalog` si `untagged` numara in catalog; `rows` e taiata la zece si
 * poarta timpul din istoric.
 *
 * `from`/`to` sunt fereastra ei, de cel mult `days` zile - mai SCURTA decat cea a
 * ecranului, fiindca e una din cele doua citiri ale tabloului care ating tabelul cel
 * mare - iar `cached_seconds` spune cat de veche poate fi cifra.
 *
 * `can_tag` e dreptul capatului de scriere (`settings.productivitee.read`), nu al
 * tabloului: cine vede tabloul nu capata prin el voia de a scrie in setari.
 */
export interface BoardUntaggedApps {
  from: string
  to: string
  days: number
  /** Fereastra e CHIAR mai scurta decat perioada aleasa? Vezi `BoardTopApps.trimmed`. */
  trimmed: boolean
  cached_seconds: number
  can_tag: boolean
  catalog: number
  untagged: number
  /** Numitorul mediilor de pe randuri: capetele (om, zi) care au raportat in fereastra. */
  person_days: number
  rows: UntaggedApp[]
}

/**
 * DISTRIBUTIA ZILELOR-OM, in sertare de o ora.
 *
 * O medie peste mii de zile-om nu spune nimic: acelasi "5h20 activ pe zi" iese
 * dintr-o echipa uniforma si dintr-una cu doua populatii. Distributia le
 * deosebeste, si mediana spune unde e mijlocul adevarat.
 *
 * Valoarea e `active` din rollup-ul pe (om, zi) - REUNIUNEA intervalelor lui in
 * ziua aia. Numai randurile din rollup intra: sumele tabelului vechi ar muta
 * zilele in alte sertare si ar schimba chiar FORMA pe care cardul o arata, fara
 * ca nimic sa se vada. Cate zile-om au ramas afara scrie in `legacy_person_days`.
 *
 * Sub `min_person_days` zile-om nu se deseneaza nimic, se scrie numarul. Ultimul
 * sertar (`max_hours`) aduna tot ce trece de el.
 */
export interface BoardDistribution {
  person_days: number
  legacy_person_days: number
  min_person_days: number
  max_hours: number
  median: number | null
  buckets: { hour: number; person_days: number }[]
}

/**
 * CAT DIN GOLURI SI-A EXPLICAT ECHIPA.
 *
 * `enabled` e `day_report_enabled` al firmei, si porneste STINS pentru toate.
 * Cat timp e stins, nimeni n-a fost intrebat nimic, si ecranul NU deseneaza
 * cardul: un "0% explicat" desenat convingator ar arata ca nimeni n-are nimic de
 * spus, cand de fapt nimeni n-a fost intrebat.
 *
 * `explained` e REUNIUNEA intervalelor scrise de om, pe (om, zi) - randurile lui
 * se pot suprapune ("sedinta 10-12" si "telefon 11-11:30" sunt amandoua
 * adevarate), si adunate ar numara aceleasi minute de doua ori. Feliile din
 * `by_kind` se reunesc fiecare in ea, deci SUMA lor poate trece de `explained`,
 * chiar prin suprapunerile dintre feluri.
 *
 * `inactive` vine din rollup si numai din ZILELE care sunt in el (`days`): in
 * sumarul vechi inactivul e scris cu un `str_replace` pe semnul minus, deci un
 * procent socotit peste el n-ar insemna nimic.
 */
export interface BoardExplained {
  enabled: boolean
  days: number
  inactive: number
  explained: number
  share: number
  entries: number
  /** Capetele (om, zi) care AU un rand scris de om - cate s-au explicat, nu cate erau. */
  person_days: number
  /**
   * NUMITORUL MEDIILOR: capetele (om, zi) din rollup peste care s-a adunat
   * inactivul. NU `person_days` de deasupra - acela numara numai capetele care au
   * un rand scris, si o medie impartita la ele ar fi crescut cu cat mai putini
   * oameni isi scriu ziua.
   */
  rollup_person_days: number
  avg_inactive: number | null
  avg_explained: number | null
  by_kind: { kind: number; label: string; seconds: number; avg_seconds: number | null; entries: number }[]
}

export interface ManagementBoard {
  from: string
  to: string
  office: number
  grain: 'day' | 'week' | 'month'
  buckets: Bucket[]
  previous: { from: string; to: string; buckets: Bucket[] }
  sources: BoardSources
  silence: BoardSilence
  top_apps: BoardTopApps
  untagged_apps: BoardUntaggedApps
  distribution: BoardDistribution
  explained: BoardExplained
  totals: Totals
  previous_totals: Totals
  offices: BoardSlice[]
  departments: BoardPanel[]
  honesty: BoardHonesty
}

export function managementQuery(from: string, to: string, office: number) {
  const p = new URLSearchParams({ from, to })
  if (office > -1) p.set('office', String(office))
  return queryOptions({
    queryKey: ['productivitee', 'management', from, to, office],
    queryFn: () => apiGet<ManagementBoard>(`/productivitee/management?${p}`),
  })
}

export function post(path: string, fields: Record<string, PostValue> = {}) {
  return apiPost<{ ok: boolean }>(path, fields)
}
